import React from 'react';
import { Button, InputNumber, Radio, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

/** used / cap，超出标红「超 +N」，≥90% 橙色 */
export const UsageTag = ({ used = 0, cap }) => {
    if (!cap) return <Text type="secondary">{used} / ∞</Text>;
    if (used > cap) return <Tag color="red">{used} / {cap} 超 +{used - cap}</Tag>;
    return <Tag color={used >= cap * 0.9 ? 'orange' : 'default'}>{used} / {cap}</Tag>;
};

/**
 * CDN 套餐的有序分组列表（Form 受控组件）。
 * value = { entries: [{ group_id, domain_cap }], overflow_group_id }
 * usage = 后端 planUsage 行（按 group_id），只读展示本套餐/分组用量。
 */
const CdnPlanGroupsEditor = ({ value, onChange, groups = [], usage = [] }) => {
    const entries = value?.entries || [];
    const overflow = value?.overflow_group_id || entries[0]?.group_id || null;
    const byId = Object.fromEntries(usage.map((u) => [u.group_id, u]));
    const groupName = (id) => groups.find((g) => g.id === id)?.name || `#${id}`;

    const emit = (next, ov = overflow) => {
        const ids = next.map((e) => e.group_id);
        onChange?.({ entries: next, overflow_group_id: ids.includes(ov) ? ov : (ids[0] || null) });
    };
    const move = (i, d) => {
        const next = [...entries];
        [next[i], next[i + d]] = [next[i + d], next[i]];
        emit(next);
    };
    const free = groups.filter((g) => !entries.some((e) => e.group_id === g.id));

    const columns = [
        { title: '#', key: 'i', width: 40, render: (_, __, i) => i + 1 },
        {
            title: '分组', key: 'g',
            render: (_, e) => {
                const g = groups.find((x) => x.id === e.group_id);
                const u = byId[e.group_id];
                return (
                    <Space size={4}>
                        <span>{groupName(e.group_id)}</span>
                        {g && g.status !== 'active' && <Tag color="red">禁用</Tag>}
                        {u && u.active && !u.valid && <Tag color="red">无节点</Tag>}
                    </Space>
                );
            },
        },
        {
            title: <Tooltip title="本套餐在该组最多放多少域名；空 = 不限">本套餐上限</Tooltip>, key: 'cap', width: 120,
            render: (_, e, i) => (
                <InputNumber min={0} size="small" style={{ width: 100 }} placeholder="不限" value={e.domain_cap || null}
                    onChange={(v) => emit(entries.map((x, j) => (j === i ? { ...x, domain_cap: v || null } : x)))} />
            ),
        },
        {
            title: '本套餐', key: 'pu', width: 110,
            render: (_, e) => <UsageTag used={byId[e.group_id]?.plan_used || 0} cap={e.domain_cap} />,
        },
        {
            title: <Tooltip title="所有套餐合计，上限在分组页设置">分组合计</Tooltip>, key: 'gu', width: 120,
            render: (_, e) => <UsageTag used={byId[e.group_id]?.group_used || 0} cap={byId[e.group_id]?.group_cap} />,
        },
        {
            title: <Tooltip title="全部满时新域名进这个组（会超上限）">溢出</Tooltip>, key: 'ov', width: 60,
            render: (_, e) => <Radio checked={overflow === e.group_id} onChange={() => emit(entries, e.group_id)} />,
        },
        {
            title: '', key: 'op', width: 110,
            render: (_, e, i) => (
                <Space size={0}>
                    <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} />
                    <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === entries.length - 1} onClick={() => move(i, 1)} />
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => emit(entries.filter((_, j) => j !== i))} />
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Table size="small" rowKey="group_id" pagination={false} columns={columns} dataSource={entries} />
            <Select
                style={{ width: 260, marginTop: 8 }}
                placeholder={<span><PlusOutlined /> 添加分组</span>}
                value={null}
                disabled={!free.length}
                options={free.map((g) => ({ value: g.id, label: g.name + (g.status !== 'active' ? '（禁用）' : '') }))}
                onChange={(id) => emit([...entries, { group_id: id, domain_cap: null }])}
            />
        </div>
    );
};

export default CdnPlanGroupsEditor;
